// Marks what the Design Review comment filter matched: search terms and designators in comment
// text, the severity tag of a finding, and the names and @mentions of the selected users.
// It uses the CSS Custom Highlight API, which paints ranges without changing the comments' DOM.

const highlightName = 'comment-filter-match';
const commentBodySelector = '.render-content.markup';

// Letters, digits, and "_" are word characters, the same rule the server uses for designators.
const wordChar = String.raw`[\p{L}\p{N}_]`;

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

// Builds one pattern for all text matches, or null when there is nothing to find in text.
function textPattern(applied: URLSearchParams): RegExp | null {
  const alternatives: string[] = [];
  for (const term of (applied.get('q') ?? '').split(/\s+/).filter(Boolean)) {
    alternatives.push(escapeRegExp(term));
  }
  for (const designator of (applied.get('designator') ?? '').split(/[\s,]+/).filter(Boolean)) {
    alternatives.push(`(?<!${wordChar})${escapeRegExp(designator)}(?!${wordChar})`);
  }
  for (const severity of applied.getAll('severity')) {
    // The tag DRCY writes, for example "[🚨 Error]", like the server's backfill migration reads it.
    alternatives.push(String.raw`\[[^\]\p{L}\p{N}_]*${escapeRegExp(severity)}\s*\]`);
  }
  return alternatives.length ? new RegExp(alternatives.join('|'), 'giu') : null;
}

// The text of an element as one string, so a match can span inline elements such as an emoji
// or bold text, with the text node and offset where each piece starts.
function indexText(root: Element): {text: string, nodes: Text[], starts: number[]} {
  const nodes: Text[] = [];
  const starts: number[] = [];
  let text = '';
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    nodes.push(node);
    starts.push(text.length);
    text += node.data;
  }
  return {text, nodes, starts};
}

function rangeForText(index: ReturnType<typeof indexText>, start: number, end: number): Range {
  const locate = (offset: number, isEnd: boolean) => {
    // The last node starting before the offset, or at it for a start boundary.
    let i = index.starts.length - 1;
    while (i > 0 && (isEnd ? index.starts[i] >= offset : index.starts[i] > offset)) i--;
    return [index.nodes[i], offset - index.starts[i]] as const;
  };
  const range = document.createRange();
  range.setStart(...locate(start, false));
  range.setEnd(...locate(end, true));
  return range;
}

function elementRange(el: Element): Range {
  const range = document.createRange();
  range.selectNodeContents(el);
  return range;
}

/** Returns the ranges to highlight in the timeline for the applied filter, in document order. */
export function collectFilterHighlights(timeline: Element, applied: URLSearchParams): Range[] {
  const ranges: Range[] = [];
  const users = new Set([...applied.getAll('author'), ...applied.getAll('user')].map((name) => name.toLowerCase()));
  const mentionedUsers = new Set(applied.getAll('user').map((name) => name.toLowerCase()));
  const pattern = textPattern(applied);

  for (const el of timeline.querySelectorAll('.comment-header [data-username]')) {
    if (users.has(el.getAttribute('data-username'))) ranges.push(elementRange(el));
  }
  for (const body of timeline.querySelectorAll(commentBodySelector)) {
    // Mentions render as links whose text is "@username".
    for (const link of body.querySelectorAll('a')) {
      const mention = /^@([^\s@]+)$/.exec(link.textContent.trim());
      if (mention && mentionedUsers.has(mention[1].toLowerCase())) ranges.push(elementRange(link));
    }
    if (!pattern) continue;
    const index = indexText(body);
    for (const match of index.text.matchAll(pattern)) {
      if (match[0]) ranges.push(rangeForText(index, match.index, match.index + match[0].length));
    }
  }
  return ranges.sort((a, b) => a.compareBoundaryPoints(Range.START_TO_START, b));
}

/** Highlights the filter matches in the timeline, replacing any earlier highlights. */
export function highlightFilterMatches(timeline: Element | null, applied: URLSearchParams) {
  if (!('highlights' in CSS)) return; // older browsers show the filtered timeline without highlights
  CSS.highlights.delete(highlightName);
  if (!timeline) return;
  const ranges = collectFilterHighlights(timeline, applied);
  if (ranges.length) CSS.highlights.set(highlightName, new Highlight(...ranges));
}
